<template>
  <div class="c-dropdown"
    v-click-outside="onClickOutside"
    v-if="selected.label !== ''"
    v-shortkey="{
      up: ['arrowup'], 
      down: ['arrowdown'],
      pageUp: ['pageup'],
      pageDown: ['pagedown'],
      moveTop: ['home'],
      moveBottom: ['end'],
      close: ['esc']
    }"
    @shortkey="keyAction">
    <label 
      v-if="label"
      class="c-dropdown__label"
      :id="generatedId">
      {{ label }}
    </label>
    <div 
      class="c-dropdown__listbox"
      v-if="responsive === 'desktop'">
      <FHButton
        class="c-dropdown__selected"
        role="button"
        ref="buttonSelect"
        aria-haspopup="listbox"
        :aria-labelledby="label ? generatedId : ''"
        @click.native="show = !show">
        <div
          class="c-dropdown__text">
          {{ selected.label }}
        </div>
        <div class="c-dropdown__icon">
          <Arrow class="c-dropdown__icon-arrow" />
        </div>
      </FHButton>

      <ul v-if="show"
        tabindex="-1"
        role="listbox"
        :aria-expanded="show"
        class="c-dropdown__dropdown"
        :style="`height: ${size}rem;`">
        <li
          v-for="(option, i) in options"
          :key="i"
          role="option"
          class="c-dropdown__option"
          :class="{ 'c-dropdown__option--selected': selectedIndex === i }">
          <FHButton
            role="button"
            tabindex="-1"
            class="c-dropdown__item"
            :class="{ 'c-dropdown__item--focused': focused === i }"
            :aria-selected="selectedIndex === i ? 'true' : 'false'"
            :value="i"
            :aria-value="i"
            @click.native="chooseOption(option, i)">
            {{ option.label }}
            <div
              v-show="selectedIndex === i"
              class="c-dropdown__item-icon">
              <Arrow class="c-dropdown__item-icon-arrow" />
            </div>
          </FHButton>
        </li>
      </ul>
    </div>

    <select v-else
      class="c-dropdown__select"
      aria-labelledby="c-dropdown__label"
      @change="$emit('item-selected', parseInt($event.target.value))">
      <option
        class="c-dropdown__option"
        v-for="(option, i) in options"
        :key="i"
        :value="i"
        :selected="selectedIndex === i"
        :aria-selected="selectedIndex === i ? 'true' : 'false'"
      >
        {{ option.label }}
      </option>
    </select>
  </div>
</template>

<script>
import Arrow from './assets/svg/Arrow'
import FHButton from '@forlagshuset/v-button'

export default {
  name: 'fh-dropdown',
  components: {
    Arrow,
    FHButton
  },

  data () {
    return {
      focused: this.selectedIndex,
      generatedId: String,
      show: false,
      selected: { label: '', value: 0 }
    }
  },

  methods: {
    chooseOption(option, i) {
      this.selected = option
      this.focused = i
      this.$emit('item-selected', i)
      this.show = false
      this.$refs.buttonSelect.$el.focus()
    },

    onClickOutside() {
      this.show = false
    },

    keyAction (event) {
      switch (event.srcKey) {
        case 'up':
          if(this.focused > 0) {
            // go to previous
            this.focused--
          } else {
            // go to the last
            this.focused = this.options.length - 1
          }
          break
        case 'down':
          if(this.focused < (this.options.length - 1)) {
            // go to next
            this.focused++
          } else {
            // go to first
            this.focused = 0
          }
          break
        case 'pageUp':
          if(this.focused > this.size) {
            // move page up
            this.focused = this.focused - this.size
          } else {
            // move to top
            this.focused = 0
          }
          break
        case 'pageDown':
          if(this.focused < (this.options.length - (this.size + 1))) {
            // move page down
            this.focused = this.focused + this.size
          } else {
            // move to bottom
            this.focused = this.options.length - 1
          }
          break
        case 'moveTop':
          // move to top
          this.focused = 0
          break
        case 'moveBottom':
          // move to bottom
          this.focused = this.options.length - 1
          break
        case 'close':
          // close dropdown
          console.log(event.srcKey)
          this.show = false
          break
        default:
      }
      document.querySelectorAll('.c-dropdown__item')[this.focused].focus()
    }
  },

  mounted () {
    this.selected = this.options[this.selectedIndex]
    this.generatedId = this.label ? this.label.toLowerCase().split(' ').join('-').concat('-'+Math.floor(1000 + Math.random() * 9000)) : ''
  },

  props: {
    options: {
      type: Array,
      default: () => []
    },
    selectedIndex: {
      type: Number,
      default: 0
    },
    responsive: {
      type: String,
      default: "desktop"
    },
    label: {
      type: String,
      default: ''
    },
    size: {
      type: Number,
      default: 5
    }
  },

  watch: {
    'options': function(value) {
      this.selected = value[this.selectedIndex]
    }
  }
}
</script>

<style lang="scss">
@mixin btnReset() {
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;

  background: transparent;

  /* inherit font & color from ancestor */
  color: inherit;
  font: inherit;

  /* Normalize `line-height`. Cannot be changed from `normal` in Firefox 4+. */
  line-height: normal;

  /* Corrects font smoothing for webkit */
  -webkit-font-smoothing: inherit;
  -moz-osx-font-smoothing: inherit;

  /* Corrects inability to style clickable `input` types in iOS */
  -webkit-appearance: none;
}

.c-dropdown__listbox {
  position: relative;
}

.c-dropdown__selected {
  @include btnReset();
  display: flex;
  cursor: pointer;
  position: relative;
  align-items: center;
  padding: 0 2rem 0 1rem;
  line-height: 2rem;
  overflow: hidden;
}

.c-dropdown__text {
  font-weight: 700;
}

.c-dropdown__icon {
  position: absolute;
  right: 0;
}

.c-dropdown__dropdown {
  position: absolute;
  top: 2em;
  left: 0;
  width: auto;
  z-index: 999;
  padding: 0 .75em 0 0;
  overflow: hidden;
  overflow-y: auto;
  background: #FFF;
}

.c-dropdown__option {
  list-style: none none;
  &--selected {
    // display: none
    outline: -2px dotted #000;
  }
}

.c-dropdown__item {
  @include btnReset();
  position: relative;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  font-weight: 700;
  font-size: 0.9em;
  padding: .45em 3em .45em 1em;

  &--focused {
    outline: 2px dotted #000;
    outline-offset: -2px;
  }
}

.c-dropdown__item-icon {
  position: absolute;
  right: 0;
  top: -.25rem;
}
</style>
